import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, take, fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilityService } from './utility.service';
import { InstallPwaDialogComponent } from '../standalone-components/install-pwa-dialog/install-pwa-dialog.component';
import { PopupService } from './popup.service';
import { IosPwaInstallDialogComponent } from '../standalone-components/ios-pwa-install-dialog/ios-pwa-install-dialog.component';
import { CustomCookieServiceService } from './custom-cookie-service.service';
import { SwUpdate } from '@angular/service-worker';
import { AuthService } from './auth.service';
import { MatDialogRef } from '@angular/material/dialog';
import { OfflineComponent } from '../standalone-components/offline/offline.component';

export interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

@Injectable({
    providedIn: 'root'
})
export class PwaService {
    private deferredPrompt: BeforeInstallPromptEvent | null = null;
    private installPromptAvailable = new BehaviorSubject<boolean>(false);
    private isInstalled = new BehaviorSubject<boolean>(false);
    private online = new BehaviorSubject<boolean>(true);
    private offlineHandlersRegistered = false;
    private offlineDialogRef: MatDialogRef<OfflineComponent> | null = null;
    private offlinePollTimer: ReturnType<typeof setInterval> | null = null;
    private connectivityProbe: Promise<boolean> | null = null;
    private visibilitySubDestroy$ = new Subject<void>();
    private updateCheckInProgress = false;
    private installDialogShown = false;

    private static readonly OFFLINE_POLL_MS = 5000;
    private static readonly PROBE_TIMEOUT_MS = 5000;

    constructor(
        private utilityService: UtilityService,
        private popupService: PopupService,
        private cookieService: CustomCookieServiceService,
        private swUpdate: SwUpdate,
        private authService: AuthService,
        private ngZone: NgZone,
    ) {
        if (this.utilityService.isBrowser()) {
            this.initializePwaInstall();
            this.initializeConnectivity();
        }
    }

    get installPromptAvailable$(): Observable<boolean> {
        return this.installPromptAvailable.asObservable();
    }

    get isInstalled$(): Observable<boolean> {
        return this.isInstalled.asObservable();
    }

    get online$(): Observable<boolean> {
        return this.online.asObservable();
    }

    /** Last verified connectivity state (see `probeConnectivity`), not the raw `navigator.onLine` hint. */
    get isOnline(): boolean {
        return this.online.value;
    }

    private initializePwaInstall(): void {
        window.addEventListener('beforeinstallprompt', (e: Event) => {
            e.preventDefault();
            this.deferredPrompt = e as BeforeInstallPromptEvent;
            this.installPromptAvailable.next(true);
        });

        window.addEventListener('appinstalled', () => {
            this.deferredPrompt = null;
            this.isInstalled.next(true);
            this.installPromptAvailable.next(false);
        });

        this.checkIfAppIsInstalled();
    }

    private initializeConnectivity(): void {
        // Starting point only; `syncConnectivity` corrects it against the server.
        this.online.next(navigator.onLine);
    }

    /**
     * `navigator.onLine` and the `online`/`offline` events are only a hint. Chrome keeps
     * reporting `false` after sleep/wake, a network switch or behind some VPNs while requests
     * work fine, and then never fires `online`. Trusting the hint alone left users stuck on the
     * offline dialog with no way out, so the dialog is opened and closed on a real server probe
     * and re-checked while it is open.
     */
    public startOfflineRouting(): void {
        if (!this.utilityService.isBrowser()) return;
        if (this.offlineHandlersRegistered) return;
        this.offlineHandlersRegistered = true;

        window.addEventListener('offline', () => this.syncConnectivity());
        window.addEventListener('online', () => this.syncConnectivity());
        // Waking the laptop or returning to the tab is exactly when the browser hint is stale.
        window.addEventListener('focus', () => {
            if (this.offlineDialogRef) this.syncConnectivity();
        });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && this.offlineDialogRef) this.syncConnectivity();
        });

        if (!navigator.onLine) this.syncConnectivity();
    }

    /**
     * Asks the server whether it is reachable. Any HTTP response counts: a 5xx means the backend
     * is unhealthy, not that the user is offline. Raw `fetch` on purpose, so the HTTP interceptor
     * does not toast, redirect or retry auth on it. Single flight; runs outside the zone so the
     * request and timer do not hold up app stability (hydration, SW registration).
     */
    public probeConnectivity(): Promise<boolean> {
        if (!this.utilityService.isBrowser()) return Promise.resolve(true);
        if (this.connectivityProbe) return this.connectivityProbe;

        this.connectivityProbe = this.ngZone.runOutsideAngular(async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), PwaService.PROBE_TIMEOUT_MS);
            try {
                await fetch(`${this.utilityService.apiBase}/_health?t=${Date.now()}`, {
                    cache: 'no-store',
                    signal: controller.signal,
                });
                return true;
            } catch (_error) {
                return false;
            } finally {
                clearTimeout(timer);
                this.connectivityProbe = null;
            }
        });
        return this.connectivityProbe;
    }

    /** Probes the server and opens or closes the offline dialog to match the verified state. */
    public async syncConnectivity(): Promise<boolean> {
        const reachable = await this.probeConnectivity();
        this.ngZone.run(() => {
            this.online.next(reachable);
            if (reachable) {
                this.closeOfflineDialog();
            } else {
                this.openOfflineDialog();
            }
        });
        return reachable;
    }

    private openOfflineDialog(): void {
        if (this.offlineDialogRef) return;
        this.offlineDialogRef = this.popupService.openResponsiveDialog(
            OfflineComponent,
            {
                maxHeight: '100vh',
                minWidth: '100vw',
                maxWidth: '100vw',
                // A browser back/forward while offline must not dismiss it; the probe will.
                closeOnNavigation: false,
            },
            true
        );
        this.offlineDialogRef.componentInstance.probe = () => this.syncConnectivity();
        this.offlineDialogRef.afterClosed().subscribe(() => {
            this.offlineDialogRef = null;
            this.stopOfflinePolling();
        });
        this.startOfflinePolling();
    }

    private closeOfflineDialog(): void {
        if (!this.offlineDialogRef) return;
        const dialogRef = this.offlineDialogRef;
        this.offlineDialogRef = null;
        this.stopOfflinePolling();
        dialogRef.close();
    }

    private startOfflinePolling(): void {
        if (this.offlinePollTimer) return;
        this.ngZone.runOutsideAngular(() => {
            this.offlinePollTimer = setInterval(() => this.syncConnectivity(), PwaService.OFFLINE_POLL_MS);
        });
    }

    private stopOfflinePolling(): void {
        if (!this.offlinePollTimer) return;
        clearInterval(this.offlinePollTimer);
        this.offlinePollTimer = null;
    }

    private checkIfAppIsInstalled(): void {
        if (this.utilityService.isPwaStandalone()) {
            this.isInstalled.next(true);
            this.installPromptAvailable.next(false);
        }
    }

    public showIosInstallInstructions(): void {
        if (!this.utilityService.isBrowser()) return;
        if (!this.authService.isSignedIn()) return; // Only show for logged-in users

        const isIos = this.utilityService.isIos;
        const isStandalone = this.utilityService.isPwaStandalone();
        const dismissed = this.cookieService.get('pwa-install-dismissed') === 'true';

        if (isIos && !isStandalone && !dismissed) {
            const dialog = this.popupService.openResponsiveDialog(IosPwaInstallDialogComponent);
            this.popupService.onClose.subscribe(() => {
                dialog.close();
            });
        }
    }

    public showInstallPrompt(): void {
        if (this.utilityService.isBrowser() && this.utilityService.isIos) {
            return;
        }
        if (!this.authService.isSignedIn()) {
            return;
        }
        if (this.utilityService.isDesktopRes()) {
            return;
        }
        const dismissed = this.cookieService.get('pwa-install-dismissed') === 'true';
        if (dismissed) {
            return;
        }
        this.installPromptAvailable$.pipe(take(1)).subscribe(available => {
            if (available && !this.installDialogShown) {
                this.installDialogShown = true;
                const dialog = this.popupService.openResponsiveDialog(InstallPwaDialogComponent);
                this.popupService.onClose.subscribe(() => {
                    dialog.close();
                });
            }
        });
    }

    async triggerInstallPrompt(): Promise<{ outcome: 'accepted' | 'dismissed'; platform: string } | null> {
        if (!this.deferredPrompt) {
            return null;
        }
        try {
            await this.deferredPrompt.prompt();
            const choiceResult = await this.deferredPrompt.userChoice;
            this.deferredPrompt = null;
            this.installPromptAvailable.next(false);
            return { outcome: choiceResult.outcome, platform: choiceResult.platform };
        } catch (error) {
            return null;
        }
    }

    public initPwa(): void {
        this.showIosInstallInstructions();
        this.showInstallPrompt();

        if (this.swUpdate.isEnabled && this.utilityService.isBrowser()) {
            this.isInstalled$
                .pipe(take(1))
                .subscribe(installed => {
                    if (installed && this.swUpdate.isEnabled) {
                        this.initializeUpdateCheckOnVisibility();
                        this.checkForUpdateAndReload();
                    }
                });
        }
    }

    private initializeUpdateCheckOnVisibility(): void {
        if (!this.utilityService.isBrowser()) {
            return;
        }
        const visibilitychange$ = fromEvent(document, 'visibilitychange');
        visibilitychange$
            .pipe(takeUntil(this.visibilitySubDestroy$))
            .subscribe(() => {
                if (document.visibilityState === 'visible' && this.swUpdate.isEnabled) {
                    this.isInstalled$
                        .pipe(take(1), takeUntil(this.visibilitySubDestroy$))
                        .subscribe(installed => {
                            if (installed) {
                                this.checkForUpdateAndReload();
                            }
                        });
                }
            });
    }

    public async checkForUpdateAndReload(): Promise<boolean> {
        if (!this.utilityService.isBrowser() || !this.swUpdate.isEnabled) {
            return false;
        }
        if (this.updateCheckInProgress) {
            return false;
        }
        this.updateCheckInProgress = true;

        try {
            const updateAvailable = await this.swUpdate.checkForUpdate();
            if (updateAvailable) {
                await this.swUpdate.activateUpdate();
                window.location.reload();
                return true;
            }
            return false;
        } catch (_error) {
            return false;
        } finally {
            this.updateCheckInProgress = false;
        }
    }

    public async activateUpdateAndReload(): Promise<void> {
        if (!this.swUpdate.isEnabled) return;
        try {
            await this.swUpdate.activateUpdate();
            window.location.reload();
        } catch (error) {
        }
    }
}